import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    TextField,
    IconButton,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FolderIcon from '@mui/icons-material/Folder';
import AuthLayout from '../../../components/AuthLayout';
import MarkdownRenderer from '../../../components/MarkdownRenderer';
import SubscriptionGate from '../../../components/SubscriptionGate';
import { useAppTheme } from '../../../App';
import { PRIMARY_ORANGE } from '../../../common/colors';
import { useVisaChat } from '../../../hooks/useVisaChat';
import { VisaType } from '../../../services/chatService';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import DocumentManagerDialog from './DocumentManagerDialog';

const GlobalTalentVisa: React.FC = () => {
    const [message, setMessage] = useState('');
    const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const { sessionId } = useParams<{ sessionId?: string }>();
    const navigate = useNavigate();
    const location = window.location.pathname;

    const { isDarkMode } = useAppTheme();

    // Determine if we're on a chat route
    const isOnChatRoute = location.includes('/chat');

    // Use our updated visa chat hook with optional sessionId from URL
    const {
        messages,
        isLoading,
        isSending,
        error,
        hasStarted,
        startChat,
        sendMessage: sendChatMessage,
        clearError: _clearError,
        currentSession,
    } = useVisaChat({
        visaType: VisaType.GLOBAL_TALENT,
        chatId: sessionId
    });

    // Handle automatic chat initialization for /chat route without sessionId
    useEffect(() => {
        if (location === '/visa/global-talent-visa/chat' && !sessionId && !isLoading && !currentSession) {
            // User is on /chat route without sessionId, create a new session
            startChat();
        }
    }, [location, sessionId, isLoading, currentSession, startChat]);

    // Redirect to chat route with session ID when a new session is created
    useEffect(() => {
        if (currentSession && isOnChatRoute && !sessionId) {
            // We have a session but no sessionId in URL, redirect to include the session ID
            navigate(`/visa/global-talent-visa/chat/${currentSession.id}`, { replace: true });
        }
    }, [currentSession, sessionId, navigate, isOnChatRoute]);

    // Auto-scroll to bottom when new messages arrive
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleStartChat = async () => {
        try {
            // Navigate to chat route, which will trigger session creation
            navigate('/visa/global-talent-visa/chat');
        } catch (err) {
            console.error('Failed to start chat:', err);
        }
    };

    const handleSend = async () => {
        if (!message.trim() || isSending) return;

        const messageToSend = message.trim();
        setMessage('');

        try {
            await sendChatMessage(messageToSend);
        } catch (err) {
            console.error('Failed to send message:', err);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const formatMessageTime = (timestamp: string | Date): string => {
        const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
        return formatDistanceToNow(date, { addSuffix: true });
    };

    const getStartChatScreen = () => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                px: 2,
            }}
        >
            <Box
                sx={{
                    maxWidth: '600px',
                    textAlign: 'center',
                    p: 4,
                    borderRadius: 3,
                    background: isDarkMode ? 'rgba(64, 65, 79, 0.3)' : 'rgba(247, 247, 248, 0.8)',
                    border: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.5)' : 'rgba(217, 217, 227, 0.8)'}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                    <SmartToyIcon sx={{
                        mr: 2,
                        color: PRIMARY_ORANGE.main,
                        fontSize: 32
                    }} />
                    <Typography variant="h5" sx={{
                        fontWeight: 600,
                        color: isDarkMode ? '#ececf1' : '#374151',
                    }}>
                        thevisa.ai Assistant
                    </Typography>
                </Box>
                <Typography sx={{
                    lineHeight: 1.6,
                    mb: 3,
                    color: isDarkMode ? '#c5c5d2' : '#6b7280',
                    fontSize: '1rem'
                }}>
                    Welcome to thevisa.ai! I&apos;m here to help you navigate the Global Talent Visa application process.
                    I can assist you with:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', mb: 4 }}>
                    <Chip
                        label="Eligibility Requirements"
                        size="medium"
                        sx={{
                            backgroundColor: isDarkMode ? 'rgba(86, 88, 105, 0.4)' : 'rgba(229, 231, 235, 0.8)',
                            color: isDarkMode ? '#d1d5db' : '#6b7280',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            border: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(209, 213, 219, 0.8)'}`,
                            '&:hover': {
                                backgroundColor: isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(229, 231, 235, 1)',
                            }
                        }}
                    />
                    <Chip
                        label="Application Process"
                        size="medium"
                        sx={{
                            backgroundColor: isDarkMode ? 'rgba(86, 88, 105, 0.4)' : 'rgba(229, 231, 235, 0.8)',
                            color: isDarkMode ? '#d1d5db' : '#6b7280',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            border: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(209, 213, 219, 0.8)'}`,
                            '&:hover': {
                                backgroundColor: isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(229, 231, 235, 1)',
                            }
                        }}
                    />
                    <Chip
                        label="Required Documents"
                        size="medium"
                        sx={{
                            backgroundColor: isDarkMode ? 'rgba(86, 88, 105, 0.4)' : 'rgba(229, 231, 235, 0.8)',
                            color: isDarkMode ? '#d1d5db' : '#6b7280',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            border: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(209, 213, 219, 0.8)'}`,
                            '&:hover': {
                                backgroundColor: isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(229, 231, 235, 1)',
                            }
                        }}
                    />
                    <Chip
                        label="Timeline & Costs"
                        size="medium"
                        sx={{
                            backgroundColor: isDarkMode ? 'rgba(86, 88, 105, 0.4)' : 'rgba(229, 231, 235, 0.8)',
                            color: isDarkMode ? '#d1d5db' : '#6b7280',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            border: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(209, 213, 219, 0.8)'}`,
                            '&:hover': {
                                backgroundColor: isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(229, 231, 235, 1)',
                            }
                        }}
                    />
                </Box>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<PlayArrowIcon />}
                    onClick={handleStartChat}
                    disabled={isLoading}
                    sx={{
                        background: PRIMARY_ORANGE.main,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        py: 1.5,
                        px: 4,
                        borderRadius: 3,
                        textTransform: 'none',
                        '&:hover': {
                            background: PRIMARY_ORANGE.dark,
                            transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                            background: isDarkMode ? 'rgba(86, 88, 105, 0.4)' : 'rgba(209, 213, 219, 0.5)',
                            color: 'rgba(255,255,255,0.5)',
                        },
                    }}
                >
                    {isLoading ? 'Starting...' : 'Start The Visa Chat'}
                </Button>
            </Box>
        </Box>
    );

    return (
        <AuthLayout>
            <SubscriptionGate
                feature="unlimited_chats"
                featureName="Unlimited Chat"
                description="Get unlimited access to our AI-powered Global Talent Visa assistant. Ask as many questions as you need to succeed with your application."
            >
                <Box
                    sx={{
                        height: 'calc(100vh - 64px)',
                        display: 'flex',
                        flexDirection: 'column',
                        background: isDarkMode ? '#212121' : '#ffffff',
                    }}
                >
                    {/* Header */}
                    <Box sx={{
                        p: { xs: 2, sm: 3 },
                        textAlign: 'center',
                        borderBottom: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.3)' : 'rgba(229, 231, 235, 0.8)'}`,
                        background: isDarkMode ? 'rgba(33, 33, 33, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'center',
                        gap: { xs: 2, sm: 0 },
                    }}>
                        {/* Document Manager Button */}
                        <Button
                            variant="outlined"
                            startIcon={<FolderIcon />}
                            onClick={() => setIsDocumentDialogOpen(true)}
                            sx={{
                                position: { xs: 'relative', sm: 'absolute' },
                                left: { sm: 20 },
                                top: { sm: '50%' },
                                transform: { sm: 'translateY(-50%)' },
                                borderColor: isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(209, 213, 219, 0.8)',
                                color: isDarkMode ? '#d1d5db' : '#6b7280',
                                fontSize: '0.875rem',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: PRIMARY_ORANGE.main,
                                    color: PRIMARY_ORANGE.main,
                                    backgroundColor: isDarkMode ? 'rgba(254, 106, 46, 0.1)' : 'rgba(254, 106, 46, 0.05)',
                                },
                            }}
                        >
                            Documents
                        </Button>

                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="h5" sx={{
                                fontWeight: 600,
                                color: isDarkMode ? '#ececf1' : '#374151',
                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                mb: 0.5,
                            }}>
                                Global Talent Visa Assistant
                            </Typography>
                            <Typography variant="body2" sx={{
                                color: isDarkMode ? '#9ca3af' : '#6b7280',
                                fontSize: '0.875rem',
                            }}>
                                AI-powered guidance for your visa journey
                            </Typography>
                        </Box>
                    </Box>

                    {/* Chat Area */}
                    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {!hasStarted ? (
                            getStartChatScreen()
                        ) : (
                            <>
                                {/* Messages */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        overflowY: 'auto',
                                        p: { xs: 2, sm: 3 },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 3,
                                    }}
                                >
                                    {messages.map((msg, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                                ...(msg.sender === 'user' && {
                                                    flexDirection: 'row-reverse',
                                                }),
                                            }}
                                        >
                                            {/* Avatar */}
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: '50%',
                                                    backgroundColor: msg.sender === 'user'
                                                        ? PRIMARY_ORANGE.main
                                                        : (isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(229, 231, 235, 0.8)'),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {msg.sender === 'user' ? (
                                                    <PersonIcon sx={{ fontSize: 20, color: 'white' }} />
                                                ) : (
                                                    <SmartToyIcon sx={{
                                                        fontSize: 20,
                                                        color: isDarkMode ? '#d1d5db' : '#6b7280'
                                                    }} />
                                                )}
                                            </Box>

                                            {/* Message Content */}
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    ...(msg.sender === 'user' && {
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                    }),
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        maxWidth: '85%',
                                                        ...(msg.sender === 'user' && {
                                                            backgroundColor: PRIMARY_ORANGE.main,
                                                            color: 'white',
                                                            borderRadius: '18px 18px 4px 18px',
                                                            px: 3,
                                                            py: 2,
                                                        }),
                                                        ...(msg.sender === 'assistant' && {
                                                            backgroundColor: 'transparent',
                                                        }),
                                                    }}
                                                >
                                                    {msg.sender === 'user' ? (
                                                        <Typography
                                                            sx={{
                                                                fontSize: '0.95rem',
                                                                lineHeight: 1.5,
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            {msg.content}
                                                        </Typography>
                                                    ) : (
                                                        <MarkdownRenderer
                                                            content={msg.content}
                                                            sx={{
                                                                '& p': {
                                                                    color: isDarkMode ? '#ececf1' : '#374151',
                                                                    fontSize: '0.95rem',
                                                                    lineHeight: 1.6,
                                                                    mb: 1.5,
                                                                    '&:last-child': { mb: 0 },
                                                                },
                                                                '& ul, & ol': {
                                                                    color: isDarkMode ? '#ececf1' : '#374151',
                                                                    fontSize: '0.95rem',
                                                                    lineHeight: 1.6,
                                                                    pl: 3,
                                                                    mb: 1.5,
                                                                },
                                                                '& li': {
                                                                    mb: 0.5,
                                                                },
                                                                '& code': {
                                                                    backgroundColor: isDarkMode ? 'rgba(86, 88, 105, 0.3)' : 'rgba(229, 231, 235, 0.5)',
                                                                    color: isDarkMode ? '#f8f8f2' : '#1f2937',
                                                                    px: 1,
                                                                    py: 0.5,
                                                                    borderRadius: 1,
                                                                    fontSize: '0.875rem',
                                                                },
                                                                '& pre': {
                                                                    backgroundColor: isDarkMode ? 'rgba(86, 88, 105, 0.3)' : 'rgba(229, 231, 235, 0.5)',
                                                                    borderRadius: 2,
                                                                    p: 2,
                                                                    overflowX: 'auto',
                                                                    mb: 1.5,
                                                                },
                                                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                                                    color: isDarkMode ? '#ececf1' : '#1f2937',
                                                                    fontWeight: 600,
                                                                    mb: 1,
                                                                    mt: 2,
                                                                    '&:first-of-type': { mt: 0 },
                                                                },
                                                            }}
                                                        />
                                                    )}

                                                    {/* Timestamp */}
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block',
                                                            mt: 1,
                                                            color: msg.sender === 'user'
                                                                ? 'rgba(255, 255, 255, 0.7)'
                                                                : (isDarkMode ? '#9ca3af' : '#9ca3af'),
                                                            fontSize: '0.75rem',
                                                            textAlign: msg.sender === 'user' ? 'right' : 'left',
                                                        }}
                                                    >
                                                        {formatMessageTime(msg.timestamp)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    ))}

                                    {/* Loading indicator */}
                                    {isLoading && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2 }}>
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: '50%',
                                                    backgroundColor: isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(229, 231, 235, 0.8)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <SmartToyIcon sx={{
                                                    fontSize: 20,
                                                    color: isDarkMode ? '#d1d5db' : '#6b7280'
                                                }} />
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <CircularProgress size={16} sx={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }} />
                                                <Typography sx={{
                                                    color: isDarkMode ? '#9ca3af' : '#6b7280',
                                                    fontSize: '0.875rem',
                                                    fontStyle: 'italic'
                                                }}>
                                                    AI is thinking...
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}

                                    <div ref={messagesEndRef} />
                                </Box>

                                {/* Error Display */}
                                {error && (
                                    <Box sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            {error}
                                        </Alert>
                                    </Box>
                                )}

                                {/* Input Area */}
                                <Box sx={{
                                    p: { xs: 2, sm: 3 },
                                    borderTop: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.3)' : 'rgba(229, 231, 235, 0.8)'}`,
                                    background: isDarkMode ? 'rgba(33, 33, 33, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: 'blur(10px)',
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        gap: { xs: 1, sm: 2 },
                                        alignItems: 'flex-end',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                    }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            maxRows={4}
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ask me anything about the Global Talent Visa..."
                                            variant="outlined"
                                            disabled={isSending || isLoading}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: isDarkMode ? 'rgba(64, 65, 79, 0.3)' : 'rgba(247, 247, 248, 0.8)',
                                                    borderRadius: 3,
                                                    fontSize: '0.95rem',
                                                    lineHeight: 1.5,
                                                    '& fieldset': {
                                                        borderColor: isDarkMode ? 'rgba(86, 88, 105, 0.5)' : 'rgba(217, 217, 227, 0.8)',
                                                        borderWidth: 1,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: isDarkMode ? 'rgba(86, 88, 105, 0.7)' : 'rgba(217, 217, 227, 1)',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: PRIMARY_ORANGE.main,
                                                        borderWidth: 2,
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: isDarkMode ? '#ececf1' : '#374151',
                                                        '&::placeholder': {
                                                            color: isDarkMode ? '#9ca3af' : '#9ca3af',
                                                            opacity: 1,
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        <IconButton
                                            onClick={handleSend}
                                            disabled={!message.trim() || isSending || isLoading}
                                            sx={{
                                                backgroundColor: (message.trim() && !isSending && !isLoading)
                                                    ? PRIMARY_ORANGE.main
                                                    : (isDarkMode ? 'rgba(86, 88, 105, 0.4)' : 'rgba(209, 213, 219, 0.6)'),
                                                color: (message.trim() && !isSending && !isLoading) ? 'white' : 'rgba(255,255,255,0.5)',
                                                width: 48,
                                                height: 48,
                                                borderRadius: 3,
                                                transition: 'all 0.2s',
                                                alignSelf: { xs: 'stretch', sm: 'auto' },
                                                mt: { xs: 1, sm: 0 },
                                                '&:hover': {
                                                    background: (message.trim() && !isSending && !isLoading)
                                                        ? PRIMARY_ORANGE.dark
                                                        : (isDarkMode ? 'rgba(86, 88, 105, 0.6)' : 'rgba(209, 213, 219, 0.7)'),
                                                    transform: (message.trim() && !isSending && !isLoading) ? 'translateY(-1px)' : 'none',
                                                },
                                                '&:disabled': {
                                                    color: 'rgba(255,255,255,0.5)',
                                                },
                                            }}
                                        >
                                            {isSending ? (
                                                <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                                            ) : (
                                                <SendIcon />
                                            )}
                                        </IconButton>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </SubscriptionGate>

            {/* Document Manager Dialog */}
            <DocumentManagerDialog
                open={isDocumentDialogOpen}
                onClose={() => setIsDocumentDialogOpen(false)}
            />
        </AuthLayout>
    );
};

export default GlobalTalentVisa; 